import { createId } from "@paralleldrive/cuid2";
import { FloppyDiskIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useCallback, useEffect, useState } from "react";

import type { FieldDefinition, SectionDefinition } from "./config";
import { KeywordsField } from "./keywords-field";
import { PeriodField } from "./period-field";

type Props = {
  definition: SectionDefinition;
  item?: Record<string, any>;
  onCancel: () => void;
  onSave: (item: Record<string, any>) => void;
};

const readValue = (values: Record<string, any>, field: FieldDefinition) => {
  const value = values[field.name];
  if (field.kind === "url") return value?.href ?? "";
  if (field.kind === "keywords") return Array.isArray(value) ? value : [];
  return String(value ?? "");
};

const isValidUrl = (value: string) => {
  if (!value.trim()) return true;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const AdditionalItemForm = ({ definition, item, onCancel, onSave }: Props) => {
  const [values, setValues] = useState<Record<string, any>>({
    ...definition.defaultValue,
    id: createId(),
    visible: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validity, setValidity] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setValues({
      ...definition.defaultValue,
      ...item,
      id: item?.id ?? createId(),
      visible: item?.visible ?? true,
    });
    setErrors({});
    setValidity({});
  }, [definition, item]);

  const change = (field: FieldDefinition, value: string | string[]) => {
    setValues((current) => ({
      ...current,
      [field.name]:
        field.kind === "url" ? { ...(current[field.name] ?? { label: "" }), href: value } : value,
    }));
    setErrors((current) => ({ ...current, [field.name]: "" }));
  };

  const setFieldValidity = useCallback((name: string, valid: boolean) => {
    setValidity((current) => (current[name] === valid ? current : { ...current, [name]: valid }));
  }, []);

  const submit = () => {
    const nextErrors: Record<string, string> = {};
    definition.fields.forEach((field) => {
      const value = readValue(values, field);
      const empty = Array.isArray(value) ? value.length === 0 : !value.trim();
      if (field.required && empty) nextErrors[field.name] = "Ce champ est obligatoire.";
      if (field.kind === "url" && typeof value === "string" && !isValidUrl(value))
        nextErrors[field.name] = "Saisissez une adresse complète, par exemple https://monsite.com.";
      if (validity[field.name] === false)
        nextErrors[field.name] = "Vérifiez les dates renseignées.";
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document
        .getElementById(`additional-field-${Object.keys(nextErrors)[0]}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    onSave(values);
  };

  const controlClass = (field: FieldDefinition) =>
    cn(
      "mt-1.5 w-full rounded-xl border bg-white px-3 text-xs font-medium text-[#071451] outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100",
      errors[field.name] ? "border-rose-400" : "border-slate-200",
      field.kind === "textarea" ? "min-h-24 resize-y py-3 leading-5" : "h-11",
    );

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_7px_25px_rgba(30,27,75,0.05)] sm:p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        {definition.fields.map((field) => {
          const value = readValue(values, field);
          const datalistId = `additional-suggestions-${field.name}`;
          const labelId = `additional-label-${field.name}`;
          const standard = !field.kind || ["text", "url", "month"].includes(field.kind);
          return (
            <div
              id={`additional-field-${field.name}`}
              key={field.name}
              className={cn(
                "block text-[11px] font-bold text-[#071451]",
                field.full && "sm:col-span-2",
              )}
            >
              <span id={labelId}>
                {field.label}
                {field.required && <span className="ml-1 text-emerald-500">*</span>}
              </span>
              {field.kind === "textarea" && (
                <textarea
                  value={value as string}
                  aria-labelledby={labelId}
                  maxLength={1000}
                  placeholder={field.placeholder}
                  onChange={(event) => change(field, event.target.value)}
                  className={controlClass(field)}
                />
              )}
              {field.kind === "keywords" && (
                <KeywordsField
                  value={value as string[]}
                  placeholder={field.placeholder}
                  suggestions={field.suggestions}
                  onChange={(next) => change(field, next)}
                />
              )}
              {field.kind === "period" && (
                <div className="mt-1.5" role="group" aria-labelledby={labelId}>
                  <PeriodField
                    value={value as string}
                    invalid={Boolean(errors[field.name])}
                    onChange={(next) => change(field, next)}
                    onValidityChange={(valid) => setFieldValidity(field.name, valid)}
                  />
                </div>
              )}
              {standard && (
                <>
                  <input
                    aria-labelledby={labelId}
                    type={field.kind === "month" ? "month" : field.kind === "url" ? "url" : "text"}
                    value={value as string}
                    placeholder={field.placeholder}
                    list={field.suggestions?.length ? datalistId : undefined}
                    inputMode={field.kind === "url" ? "url" : undefined}
                    autoComplete={field.kind === "url" ? "url" : "off"}
                    onChange={(event) => change(field, event.target.value)}
                    className={controlClass(field)}
                  />
                  {field.suggestions?.length ? (
                    <datalist id={datalistId}>
                      {field.suggestions.map((suggestion) => (
                        <option key={suggestion} value={suggestion} />
                      ))}
                    </datalist>
                  ) : null}
                </>
              )}
              {errors[field.name] && (
                <span className="mt-1 block text-[9px] font-medium text-rose-600">
                  {errors[field.name]}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="xs:flex-row xs:justify-end mt-5 flex flex-col-reverse gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
        >
          <XIcon size={14} /> Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-5 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5"
        >
          <FloppyDiskIcon size={15} weight="bold" />{" "}
          {item ? "Enregistrer les modifications" : `Ajouter ${definition.singular}`}
        </button>
      </div>
    </div>
  );
};
